/**
 * StarToggle — "one of my guys", the one implementation.
 *
 * 🔴 THREE CALL SITES, ONE COMPONENT, AND IT MUST STAY THAT WAY. The player
 * list row, the shared PlayerCard portal and the Draft Plan target card all
 * render this. Three hand-rolled buttons would drift on the glyph, the title
 * text, the colour and — worst — on which of ★/☆ means starred, which is the
 * kind of divergence nobody notices until two surfaces disagree on screen.
 *
 * Presentational on purpose: it takes `starred` and `onToggle` and owns no
 * state. The star sheet lives in usePlanState and reaches each surface its own
 * way (props in the list and the card grid, the PlayerCard context in the
 * portal); a component that fetched its own state could only serve one of them.
 *
 * TWO VARIANTS, because two backgrounds:
 *
 *   boxed   — a bordered square that sits in a row of other controls. Used in
 *             the player list and on the PlayerCard header.
 *   overlay — a filled circle that sits ON a player's headshot. It carries its
 *             own dark disc because a hollow glyph over a photograph is
 *             invisible against a light jersey and unreadable against a busy
 *             one. Used on the Draft Plan target card's avatar.
 */

import React from "react";
import { theme } from "../data/theme";

export const STAR_ON = "★";    // ★ filled — starred
export const STAR_OFF = "☆";   // ☆ hollow — not starred

export default function StarToggle({
  starred = false,
  onToggle,
  name = "",
  size = 20,
  variant = "boxed",
  // 🔴 Every surface names its own instance. Three surfaces can be on screen at
  // once — a target card, the list behind it and the card portal over both —
  // and a test that cannot tell them apart is a test that passes on the wrong
  // button.
  testId = "star-toggle",
  style,
}) {
  const overlay = variant === "overlay";
  const who = name ? ` ${name}` : "";
  const base = {
    width: size, height: size, flexShrink: 0, padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    lineHeight: 1,
    // Scaled off the button rather than fixed per call site, so a size change
    // cannot leave a glyph rattling around inside its own control.
    fontSize: Math.max(10, Math.round(size * 0.6)),
    cursor: "pointer",
  };
  const chrome = overlay
    ? {
        borderRadius: "50%",
        // Opaque disc in BOTH states. A translucent one would tint with
        // whatever headshot happens to be underneath, so the unstarred control
        // would look different on every card in the row.
        background: starred ? theme.colors.bg.deep : "rgba(10,15,26,0.78)",
        border: `1px solid ${starred ? theme.colors.star : "rgba(255,255,255,0.22)"}`,
        color: starred ? theme.colors.star : theme.colors.text.muted,
      }
    : {
        borderRadius: theme.radius.sm,
        background: starred ? theme.helpers.starBg() : "transparent",
        border: `1px solid ${starred ? theme.colors.star : theme.colors.border.default}`,
        color: starred ? theme.colors.star : theme.colors.text.faint,
      };

  return (
    <button
      type="button"
      aria-pressed={starred}
      data-testid={testId}
      title={starred ? `Unstar${who}` : `Star${who} — one of my guys`}
      onClick={(e) => {
        // 🔴 The Draft Plan target card's EMPTY branch has a root onClick that
        // selects the cell, and the list row is inside a clickable list. A star
        // must never also do the surrounding thing.
        e.stopPropagation();
        if (onToggle) onToggle();
      }}
      style={{ ...base, ...chrome, ...style }}
    >{starred ? STAR_ON : STAR_OFF}</button>
  );
}
